import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Send } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useLang, useL } from "@/i18n";
import { settingsQueryKey, fetchSettings } from "@/lib/queries";
import { toast } from "sonner";

const LOGO = "/assets/kreyola-logo.webp";

export default function Footer() {
  const { t, lang } = useLang();
  const { data: settings } = useQuery({ queryKey: settingsQueryKey, queryFn: fetchSettings });
  const [email, setEmail] = useState("");

  const cols = [
    { title: lang === "fr" ? "Boutique" : "Shop", links: [["/shop", t("nav.shop")], ["/shop?tag=new", t("home.newArrivals")], ["/shop?tag=bestseller", t("home.bestsellers")], ["/shop?tag=special", t("home.offers")]] },
    { title: lang === "fr" ? "Entreprise" : "Company", links: [["/about", t("nav.about")], ["/contact", t("nav.contact")]] },
    { title: lang === "fr" ? "Aide" : "Help", links: [["/track-order", t("tracking.title")], ["/contact", "FAQ"], ["/contact", lang === "fr" ? "Livraison" : "Shipping"], ["/contact", lang === "fr" ? "Retours" : "Returns"], ["/contact", lang === "fr" ? "Confidentialité" : "Privacy Policy"], ["/contact", lang === "fr" ? "Conditions" : "Terms & Conditions"]] },
  ];

  const subscribe = (e) => {
    e.preventDefault();
    if (email) {
      toast.info(lang === "fr"
        ? "L’inscription à la newsletter n’est pas encore disponible. Votre adresse n’a pas été enregistrée."
        : "Newsletter signup is not available yet. Your email address has not been saved.");
    }
  };

  return (
    <footer className="relative mt-20 overflow-hidden bg-navy text-cream" data-testid="footer">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #D4AF37 0, transparent 40%), radial-gradient(circle at 80% 60%, #EA580C 0, transparent 40%)" }} />
      <div className="relative mx-auto max-w-[1400px] px-4 py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="Kreyola Boutik" className="h-16 w-16 object-contain" />
              <span className="font-display text-2xl font-bold">Kreyola <span className="text-gradient-gold">Boutik</span></span>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-cream/70">
              {lang === "fr"
                ? "Produits haïtiens authentiques, livrés partout aux États-Unis. Lakay toujou nan kè nou."
                : "Authentic Haitian products, delivered across the USA. Lakay toujou nan kè nou."}
            </p>
            <p className="mt-5 font-script text-2xl text-gold">Lakay toujou nan kè nou</p>
            <div className="mt-5 flex gap-3">
              {[[Facebook, settings?.socials?.facebook], [Instagram, settings?.socials?.instagram], [Youtube, settings?.socials?.youtube]].map(([Icon, url], i) => (
                <a key={i} href={url || "#"} className="grid h-10 w-10 place-items-center rounded-full border border-white/15 transition hover:border-gold hover:text-gold" aria-label="social">
                  <Icon size={17} />
                </a>
              ))}
            </div>
          </div>

          {cols.map((c) => (
            <div key={c.title}>
              <h4 className="mb-4 font-head text-sm font-bold uppercase tracking-wider text-gold">{c.title}</h4>
              <ul className="space-y-2.5">
                {c.links.map(([to, label], i) => (
                  <li key={i}><Link to={to} className="text-sm text-cream/70 transition hover:text-gold">{label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
          <div className="flex flex-col items-center gap-5 text-center md:flex-row md:justify-between md:text-left">
            <div>
              <h3 className="font-display text-2xl font-bold text-cream">{t("footer.newsletter")}</h3>
              <p className="mt-1 text-sm text-cream/60">{lang === "fr" ? "Nouveautés, offres et histoires d'Haïti." : "New arrivals, offers and stories from Haiti."}</p>
            </div>
            <form onSubmit={subscribe} className="flex w-full max-w-md gap-2" data-testid="newsletter-form">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("footer.emailPlaceholder")} data-testid="newsletter-input"
                className="w-full rounded-full border border-white/15 bg-navy-deep px-4 py-3 text-sm text-cream outline-none focus:border-gold" />
              <button type="submit" data-testid="newsletter-submit" className="btn-gold flex shrink-0 items-center gap-2 rounded-full px-5 py-3 text-sm font-bold">
                <Send size={15} /> <span className="hidden sm:inline">{t("footer.subscribe")}</span>
              </button>
            </form>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-xs text-cream/50 sm:flex-row">
          <p data-testid="footer-copyright">
            © {new Date().getFullYear()} Kreyola Boutik. {lang === "fr" ? "Tous droits réservés." : "All Rights Reserved."}
            <span className="mx-2 text-cream/30">|</span>
            {lang === "fr" ? "Créé par" : "Created by"}{" "}
            <a
              href={`https://wa.me/50937007500?text=${encodeURIComponent("Hello IG Design Lab! 👋 I discovered your work through the Kreyola Boutik website. I’m interested in creating a website for my business and would love to learn more about your services.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-ig-design-lab-link"
              className="font-semibold text-cream/70 transition-colors hover:text-gold"
            >
              IG Design Lab
            </a>
          </p>
          <p>{lang === "fr" ? "Produits Haïtiens Authentiques · Livrés aux États-Unis" : "Authentic Haitian Products · Delivered Across the USA"}</p>
        </div>
      </div>
    </footer>
  );
}
